/* eslint-disable react/display-name */
import React, { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { isEmpty, isNil, isUndefined, isNaN, noop } from 'lodash';
import validator from 'validator';
import { toast } from 'react-toastify';
import limitedEvaluate, { mathScope } from 'src/services/configuration/MathJS.service';
import ServiceContainer from 'src/ServiceContainer';
import { InputCharacterWhitelist } from 'src/common-ui/components/Inputs/InputGeneric/InputGeneric';
import { ICellEditorParams } from '@ag-grid-community/core';

/** A note here that this component does triple duty as an ag-grid editor, renderer, and as a regular component
 * Therefore, there are places where the component conditionally checks it's props to see
 * whether the originating component is aggrid or just regular react
 */
// export interface InputIntegerProps extends CustomCellEditorProps {
export interface InputIntegerProps {
  value: string | number | null;
  onChange: (text: string | number | null) => void;
  onBlur?: (prop: any) => any;
  editable?: boolean;
  valid?: boolean;
  autoFocus?: boolean;
  className?: string;
  nullable?: boolean;
  id?: string;
}

export default forwardRef((props: InputIntegerProps, ref) => {
  const { editable, onChange, nullable, value: propValue, id } = props;

  /** If rendered by aggrid and doesn't have an `onChange` passed in by `cellEditorSelector` then this is `noop`
   * because we use imperative aggrid components, in which case the `useImperativeRef` function takes the places of
   * the `handleOnBlur`
   * See https://www.ag-grid.com/archive/31.2.0/react-data-grid/component-cell-editor-imperative-react/
   */
  const maybeOnChange = !onChange ? noop : onChange;
  // check for ag-grid api reference to determine if component is being rendered within a grid
  const isGridEditor = !isNil((props as unknown as ICellEditorParams).api);

  const [localValue, setLocalValue] = useState(() => propValue);

  useEffect(() => {
    setLocalValue(propValue);
  }, [propValue]);

  /** Just used to check the validator before bluring and sending the updated value out */
  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const trimmedValue = newValue.trim();

    if (validator.isNumeric(trimmedValue)) {
      setLocalValue(trimmedValue);
    } else {
      const whitelistedValue = validator.whitelist(trimmedValue, InputCharacterWhitelist.basicMath);
      setLocalValue(whitelistedValue);
    }
  };

  /** Handles ending the edit when rendered in react
   * Note that when rendered by aggrid, we imperatively call this function and use
   * the return for `getValue`
   */
  const handleOnBlur = useCallback(() => {
    if ((isNil(localValue) || isEmpty(localValue)) && !nullable) {
      setLocalValue(0);
      maybeOnChange(0);
      return localValue;
    }

    // onChange can't handle undefined, default to null
    let nextValue = isUndefined(propValue) ? null : propValue;
    try {
      const internalValue = localValue?.toString() || '';

      // outputs NaN for anything equation like
      // also prevents numbers from being handled in limitedEvaluate below (like 0) resulting in null
      const parsedInternalValue = Number(internalValue);

      // check internalValue for empty string because passing empty string to Number(...) above converts to 0 which is unwanted
      if (isEmpty(internalValue) || isNaN(parsedInternalValue)) {
        const evaluated = limitedEvaluate ? limitedEvaluate(internalValue, mathScope) : null;
        nextValue = !isNil(evaluated) ? evaluated : null;
      } else {
        nextValue = parsedInternalValue;
      }
    } catch (e) {
      toast.info('Unable to parse the input expression, the edit has been reverted', {
        position: toast.POSITION.TOP_RIGHT,
      });
      // FIXME: uncommenting this causes the vitest implementation to crash,
      // it seems to be because I am not mocking the service properly or it could be an imports order issue
      // Unfortunately haven't figured this out yet with vite & msw (https://github.com/mswjs/msw)
      ServiceContainer.loggingService.info(
        `Error: unable to parse an expression the user entered '${localValue}'`,
        JSON.stringify((e as Error).stack),
      );
    } finally {
      if (!isGridEditor) {
        // only when outside of ag-grid, otherwise you lose the persist editor value on tab functionality in TabbedReceiptsAdjCalculator
        // reset local to make sure we are in sync with the prop values before updating
        setLocalValue(propValue);
      }
      maybeOnChange(nextValue);
      return nextValue;
    }
  }, [localValue, nullable, propValue, maybeOnChange, isGridEditor]);

  /* Component Cell Editor Lifecycle methods */
  // This is the "old" way that aggrid handled functional components,
  // but this will be removed in aggrid 32
  // see https://www.ag-grid.com/archive/31.2.0/react-data-grid/upgrading-to-ag-grid-31-1/#migrating-custom-components-to-use-reactivecustomcomponents-option
  // when we upgrade to 32
  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return handleOnBlur();
      },
    };
  });

  const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  // input's `value` attribute can't handle null,
  // also won't update if passing undefined, so instead set to empty string so rendered data updates
  const finalValue = isNil(localValue) ? '' : localValue;

  // `autofocus` needs to be dynamic here, because when rendered in grids we need it to autofocus
  // (because you only ever have one cell up at a time), but elsewhere we need it to *not* autofocus,
  // otherwise the components fight each other for focus, and call `onBlur` constantly
  // FIXME we will need to be better about detecting this as this is likely brittle.
  // `SalesAdjustmentEditor` and `TabbedReceiptsAdjustmentCalculator` will need to be retested, along with `ConfigurableDataModal`
  const needsAutoFocus = onChange == null ? true : false;

  return (
    <input
      id={id}
      data-qa={'InputInteger'}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      value={finalValue}
      disabled={!editable}
      onKeyUp={onKeyUp}
      autoFocus={needsAutoFocus}
    />
  );
});
